import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useStore } from 'effector-react';
import { Empty, Modal, Spin, Table } from 'antd';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import moment from 'moment';

import IUser from '@interfaces/IUser';
import useTranslate from '@hooks/useTranslate';
import componentContent from '@components/SpamUserCard/SpamUserCard.content.json';
import userSuspiciousChats, {
  getList as getUserSuspiciousChats,
  reset as resetUserSuspiciousChats,
  IUserSuspiciousChat,
} from '@stores/user-suspicious-chats';
import getChatRecipientName from '@helpers/getChatRecipientName';
import css from './UserSuspiciousChatsTrigger.module.less';

interface UserSuspiciousChatsTriggerProps {
  user?: IUser | null;
}

interface ExpandableCommentProps {
  text?: string;
}

const ExpandableComment: FunctionComponent<ExpandableCommentProps> = ({ text }) => {
  const content = text || '-';
  const textRef = useRef<HTMLDivElement | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [canToggle, setCanToggle] = useState(false);

  useEffect(() => {
    setIsExpanded(false);
    setCanToggle(false);
  }, [content]);

  useEffect(() => {
    const textElement = textRef.current;

    if (!textElement || isExpanded) return;

    setCanToggle(textElement.scrollHeight > textElement.clientHeight + 1);
  }, [content, isExpanded]);

  const handleToggle = useCallback(() => {
    setIsExpanded((value) => !value);
  }, []);

  return (
    <div className={css.commentCell}>
      <div
        ref={textRef}
        className={`${css.commentText} ${isExpanded ? '' : css.commentCollapsed}`}
      >
        {content}
      </div>

      {canToggle ? (
        <button
          aria-label={isExpanded ? 'Collapse comment' : 'Expand comment'}
          className={css.toggleButton}
          onClick={handleToggle}
          type='button'
        >
          {isExpanded ? <UpOutlined /> : <DownOutlined />}
        </button>
      ) : null}
    </div>
  );
};

const getStringValue = (value: unknown): string => {
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);

  return '';
};

const getRowId = (item: IUserSuspiciousChat, index: number) => (
  item.id ||
  item.chatId ||
  item.messageId ||
  `${item.created || item.createdAt || item.messageCreatedAt || 'row'}-${index}`
);

const UserSuspiciousChatsTrigger: FunctionComponent<UserSuspiciousChatsTriggerProps> = ({
  user,
}) => {
  const content = useTranslate(componentContent);
  const { list } = useStore(userSuspiciousChats);

  const [isOpened, setIsOpened] = useState(false);
  const [isModalLoading, setIsModalLoading] = useState(false);

  const handleOpen = useCallback(async () => {
    if (!user?.id) return;

    resetUserSuspiciousChats();
    setIsOpened(true);
    setIsModalLoading(true);

    try {
      await getUserSuspiciousChats(user.id);
    } finally {
      setIsModalLoading(false);
    }
  }, [user?.id]);

  const handleClose = useCallback(() => {
    setIsOpened(false);
    setIsModalLoading(false);
  }, []);

  const dataSource = useMemo(
    () =>
      list.map((item: IUserSuspiciousChat, index: number) => ({
        id: getRowId(item, index),
        created: item.created || item.createdAt || item.messageCreatedAt,
        comment:
          getStringValue(item.comment) ||
          getStringValue(item.text) ||
          getStringValue(item.messageText) ||
          '-',
        nickname: getStringValue(item.nickname) || getChatRecipientName(item),
        reason: getStringValue(item.reason) || getStringValue(item.type) || '-',
      })),
    [list],
  );

  const columns = useMemo(
    () => [
      {
        key: 'created',
        dataIndex: 'created',
        title: content.created,
        width: 155,
        render: (value: string) => (value ? moment(value).format('DD.MM.YYYY HH:mm') : '-'),
      },
      {
        key: 'comment',
        dataIndex: 'comment',
        title: content.comment,
        render: (value: string) => <ExpandableComment text={value} />,
      },
      {
        key: 'nickname',
        dataIndex: 'nickname',
        title: content.nickname,
        width: 170,
        render: (value: string) => value || '-',
      },
      {
        key: 'reason',
        dataIndex: 'reason',
        title: content.reason,
        width: 220,
        render: (value: string) => value || '-',
      },
    ],
    [content],
  );

  const count = Number(user?.suspiciousChatsCount || 0);
  const triggerText = `${content.suspiciousChats}: ${count}`;

  return (
    <div className={css.trigger}>
      {count > 0 ? (
        <a
          className={`${css.count} ${css.active}`}
          href='#'
          onClick={(event) => {
            event.preventDefault();
            handleOpen();
          }}
        >
          {triggerText}
        </a>
      ) : (
        <span className={css.count}>{triggerText}</span>
      )}

      <Modal
        visible={isOpened}
        title={
          <div>
            <div>{`${content.suspiciousChats}: ${count}`}</div>
            {user?.nickname ? (
              <a
                href={`/user-list/${user.id}`}
                target='_blank'
                rel='noreferrer'
              >
                {user.nickname}
              </a>
            ) : null}
          </div>
        }
        footer={null}
        onCancel={handleClose}
        width={1200}
        destroyOnClose
        keyboard
      >
        {isModalLoading ? (
          <div
            style={{
              minHeight: 240,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Spin size='large' />
          </div>
        ) : dataSource.length > 0 ? (
          <Table
            rowKey='id'
            dataSource={dataSource}
            columns={columns}
            pagination={{
              pageSizeOptions: ['10', '20', '30'],
              showSizeChanger: true,
              position: ['topRight'],
              defaultPageSize: 10,
            }}
          />
        ) : (
          <Empty imageStyle={{ height: 50 }} />
        )}
      </Modal>
    </div>
  );
};

export default UserSuspiciousChatsTrigger;
